import { Button, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';

import closeIcon from '../../../../assets/svg/close-icon.svg';
import logoIcon from '../../../../assets/svg/tencent-logo.svg';
import styles from './index.module.scss';
import { UploadCert } from './upload';

import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FieldData } from 'rc-field-form/lib/interface';
import { CompanyInternalAuthDrawer } from './internal-cert';
import {
  IAPICompanyFullInfo,
  StudioCertifiedType,
} from '../../../../model/api-modules';
import { ICompanyFormInfo, ICompanyRejectReason } from '../../../../model';
import { strToJson } from '../../../../lib';
import getFormatMessage from '../../../../locales/get-format-message';
import { getCompanyFileUrl } from '../../lib/company-cos';

interface IProps {
  data?: IAPICompanyFullInfo | null;
  businessLicenseUrl?: string;
  readOnly?: boolean;
  certType: StudioCertifiedType;
  kpiCodeOptions: { label: string; value: string }[];
  onOK?: (info?: ICompanyFormInfo) => void;
  onClose: (info?: ICompanyFormInfo) => void;
}

interface IRejectReasonMap {
  [key: string]: string;
}

export const CompanyAuthDrawer = (props: IProps) => {
  const {
    data: companyInfo,
    readOnly,
    businessLicenseUrl,
    certType,
    onClose,
  } = props;
  const [form] = useForm<ICompanyFormInfo>();

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [rejectReasons, setRejectReasons] = useState<IRejectReasonMap>({});

  useEffect(() => {
    init();
  }, [companyInfo?.id]);

  const init = async () => {
    if (!companyInfo) {
      return;
    }

    if (certType === StudioCertifiedType.InternalKpiCode) {
      return;
    }
    const contactInfo = companyInfo.Contacts?.split('|');

    const licenseUrl =
      businessLicenseUrl ||
      (await getCompanyFileUrl(companyInfo.BusinessLicenseImageUrl));

    form.setFieldsValue({
      name: companyInfo.Name,
      description: companyInfo.Description,
      creditCode: companyInfo.CreditCode,
      uploadValue: licenseUrl
        ? {
            fileName: companyInfo.BusinessLicenseImageUrl,
            url: licenseUrl,
          }
        : undefined,
      contactName: contactInfo?.[0],
      contactIDCode: contactInfo?.[1],
      contactPhone: contactInfo?.[2],
    });

    const rejectItems =
      strToJson<ReadonlyArray<ICompanyRejectReason>>(
        companyInfo.ManualRejectReason || ''
      ) || [];

    const rejectMap = rejectItems.reduce<IRejectReasonMap>((acc, item) => {
      if (item.type) {
        acc[item.type] = item.reason;
      }
      return acc;
    }, {});
    setRejectReasons(rejectMap);
  };

  const onFieldsChange = (changedFields: FieldData[]) => {
    const newReasons = { ...rejectReasons };
    const changedFieldName = String(changedFields[0]?.name);
    if (changedFieldName === 'uploadValue') {
      newReasons.businessLicenseImageUrl = '';
    } else {
      newReasons[changedFieldName] = '';
    }
    setRejectReasons(newReasons);
  };

  const getErrTips = (type: string) => {
    if (!rejectReasons[type]) {
      return;
    }
    return (
      <>
        {!!rejectReasons[type] && (
          <div className={styles.errTips}>{rejectReasons[type]}</div>
        )}
      </>
    );
  };

  const submit = async () => {
    const formData = await form.validateFields();
    const info: ICompanyFormInfo = {
      name: formData.name,
      description: formData.description || '',
      creditCode: formData.creditCode,
      uploadValue: formData.uploadValue,
      contactName: formData.contactName,
      contactIDCode: formData.contactIDCode,
      contactPhone: formData.contactPhone,
    };
    setSubmitting(true);
    await props.onOK?.(info);
    setSubmitting(false);
  };

  const closeHandler = () => {
    const formData = form.getFieldsValue();
    const info: ICompanyFormInfo = {
      name: formData.name,
      description: formData.description || '',
      creditCode: formData.creditCode,
      uploadValue: formData.uploadValue,
      contactName: formData.contactName,
      contactIDCode: formData.contactIDCode,
      contactPhone: formData.contactPhone,
    };
    onClose(info);
  };

  if (certType === StudioCertifiedType.InternalKpiCode) {
    return <CompanyInternalAuthDrawer {...props} onClose={() => onClose()} />;
  }

  return (
    <Drawer
      className={styles.drawer}
      closable={false}
      onClose={closeHandler}
      visible={true}
      headerStyle={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        marginLeft: '3px',
      }}
      width={720}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{getFormatMessage('company_auth')}</span>
          <img
            src={closeIcon}
            className="sync-close-icon"
            onClick={closeHandler}
          />
        </div>
      }
      footer={
        readOnly ? (
          <></>
        ) : (
          <div>
            <Button className="gc-btn sub-button" onClick={closeHandler}>
              {getFormatMessage('cancel')}
            </Button>
            <Button
              className="gc-btn gc-btn-primary"
              type="primary"
              onClick={submit}
              loading={submitting}
            >
              {getFormatMessage('submit')}
            </Button>
          </div>
        )
      }
    >
      <div className={styles.authForm}>
        <div className={styles.headInfo}>
          <span>{getFormatMessage('company_cooper')}</span>
          <img src={logoIcon} />
        </div>
        <Form
          form={form}
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 24 }}
          layout="vertical"
          onFieldsChange={onFieldsChange}
        >
          <div className={styles.formGroupName}>
            {getFormatMessage('company_basic_info')}
          </div>
          <Form.Item
            label={getFormatMessage('company_name')}
            name="name"
            rules={[
              {
                required: !readOnly,
                validator: (rule, value, callback) => {
                  if (
                    !/^[\u4e00-\u9fa5A-Za-z0-9()（）【】《》]{1,100}$/.test(
                      value
                    )
                  ) {
                    callback('输入格式不正确');
                  }
                  callback();
                },
              },
            ]}
            extra={getErrTips('name')}
          >
            <Input disabled={readOnly} />
          </Form.Item>

          <div className={classNames('ant-form-item-label')}>
            <label>{getFormatMessage('company_area')}</label>
          </div>
          <div className={styles.authArea}>
            <span>{getFormatMessage('company_area_cn')}</span>
            {!readOnly && <span>{getFormatMessage('company_area_info')}</span>}
          </div>

          <Form.Item
            label={getFormatMessage('company_desc')}
            name="description"
            extra={getErrTips('description')}
          >
            <Input.TextArea disabled={readOnly} maxLength={500} />
          </Form.Item>

          <div className={styles.formGroupName}>
            {getFormatMessage('company_cert')}
          </div>
          <Form.Item
            label={getFormatMessage('company_credit_code')}
            name="creditCode"
            extra={
              <>
                {getErrTips('creditCode')}
                <span>{getFormatMessage('company_credit_code_info')}</span>
              </>
            }
            rules={[
              {
                required: !readOnly,
                validator: (rule, value, callback) => {
                  if (!/^[0-9A-Z]{18}$/.test(value)) {
                    callback(getFormatMessage('company_input_wrong'));
                  }
                  callback();
                },
              },
            ]}
          >
            <Input disabled={readOnly} />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('company_license')}
            name="uploadValue"
            rules={[{ required: !readOnly }]}
            extra={getErrTips('businessLicenseImageUrl')}
          >
            <UploadCert disabled={readOnly} />
          </Form.Item>

          <div className={styles.formGroupName}>
            {getFormatMessage('company_contact_man')}
          </div>

          <div className={styles.formContactArea}>
            <Form.Item
              label={getFormatMessage('company_contact_name')}
              name="contactName"
              rules={[
                {
                  required: !readOnly,
                  validator: (rule, value, callback) => {
                    if (!/^[\u4e00-\u9fa5A-Za-z\s]+$/.test(value)) {
                      callback(getFormatMessage('company_input_wrong'));
                    }
                    callback();
                  },
                },
              ]}
              extra={getErrTips('contactName')}
            >
              <Input disabled={readOnly} maxLength={50} />
            </Form.Item>
            <Form.Item
              label={getFormatMessage('company_contact_id')}
              name="contactIDCode"
              rules={[
                {
                  required: !readOnly,
                  validator: (rule, value, callback) => {
                    if (!/^(?:\d{15}|\d{17}[\dX])$/.test(value)) {
                      callback(getFormatMessage('company_input_wrong'));
                    }
                    callback();
                  },
                },
              ]}
              extra={getErrTips('contactIDCode')}
            >
              <Input disabled={readOnly} />
            </Form.Item>
            <Form.Item
              label={getFormatMessage('company_contact_phone')}
              name="contactPhone"
              rules={[
                {
                  required: !readOnly,
                  validator: (rule, value, callback) => {
                    if (!/^1[3-9]\d{9}$/.test(value)) {
                      callback(getFormatMessage('company_input_wrong'));
                    }
                    callback();
                  },
                },
              ]}
              extra={getErrTips('contactPhone')}
            >
              <Input addonBefore="+086" disabled={readOnly} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Drawer>
  );
};
